import { normalizeList } from '../../../lib/admin-api/index.js';

export const CURRENCY_RATE_DECIMAL_PATTERN = /^\d{1,6}\.\d{1,6}$/;
export const CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/;

function trimValue(value) {
  return String(value || '').trim();
}

function asStringOrNull(value) {
  if (value === null || value === undefined) {
    return null;
  }

  return String(value);
}

function asObjectOrNull(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function normalizeRates(payload) {
  if (Array.isArray(payload?.rates)) {
    return payload.rates;
  }

  if (payload?.rates && typeof payload.rates === 'object') {
    return Object.values(payload.rates);
  }

  return normalizeList(payload);
}

function mapCurrencyRateItemToDto(item) {
  return {
    currencyBase: trimValue(item?.currencyBase ?? item?.currency_base).toUpperCase(),
    currency: trimValue(item?.currency).toUpperCase(),
    rateBank: asStringOrNull(item?.rateBank ?? item?.rate_bank),
    rateUser: asStringOrNull(item?.rateUser ?? item?.rate_user),
    effectiveRate: asStringOrNull(item?.effectiveRate ?? item?.effective_rate),
    isOverridden: Boolean(item?.isOverridden ?? item?.is_overridden),
    modified: asStringOrNull(item?.modified),
    lastChangedAt: asStringOrNull(item?.lastChangedAt ?? item?.last_changed_at),
    lastChangedBy: asObjectOrNull(item?.lastChangedBy ?? item?.last_changed_by),
    lastReason: asStringOrNull(item?.lastReason ?? item?.last_reason),
  };
}

function mapLastReloadToDto(item) {
  return {
    status: trimValue(item?.status || 'never') || 'never',
    startedAt: asStringOrNull(item?.startedAt ?? item?.started_at),
    completedAt: asStringOrNull(item?.completedAt ?? item?.completed_at),
    loadedCount: Number(item?.loadedCount ?? item?.loaded_count ?? 0),
    errorMessage: asStringOrNull(item?.errorMessage ?? item?.error_message),
  };
}

function mapHistoryItemToDto(item) {
  return {
    action: trimValue(item?.action),
    oldRateBank: asStringOrNull(item?.oldRateBank ?? item?.old_rate_bank),
    newRateBank: asStringOrNull(item?.newRateBank ?? item?.new_rate_bank),
    oldRateUser: asStringOrNull(item?.oldRateUser ?? item?.old_rate_user),
    newRateUser: asStringOrNull(item?.newRateUser ?? item?.new_rate_user),
    reason: asStringOrNull(item?.reason),
    actor: asObjectOrNull(item?.actor),
    createdAt: asStringOrNull(item?.createdAt ?? item?.created_at),
  };
}

function resolveFieldErrorParameter(parameter) {
  const normalized = trimValue(parameter);
  if (!normalized) {
    return '';
  }

  const segments = normalized
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .map((item) => item.trim())
    .filter(Boolean);

  return segments[segments.length - 1] || '';
}

export function mapCurrencyRatesResponseToDto(payload) {
  return {
    rates: normalizeRates(payload).map((item) => mapCurrencyRateItemToDto(item)),
    lastReload: mapLastReloadToDto(payload?.lastReload ?? payload?.last_reload),
    result: Boolean(payload?.result),
  };
}

export function mapCurrencyRateResponseToDto(payload) {
  return {
    rate: payload?.rate ? mapCurrencyRateItemToDto(payload.rate) : null,
    result: Boolean(payload?.result),
  };
}

export function mapCurrencyRateHistoryResponseToDto(payload) {
  const history = Array.isArray(payload?.history)
    ? payload.history
    : normalizeList(payload?.history);

  return {
    history: history.map((item) => mapHistoryItemToDto(item)),
    total: Number(payload?.total ?? history.length),
    result: Boolean(payload?.result),
  };
}

export function validateCurrencyRateOverridePayload(payload) {
  const errors = {};
  const rate = trimValue(payload?.rate);
  const reason = trimValue(payload?.reason);

  if (!rate) {
    errors.rate = 'Rate is required.';
  } else if (!CURRENCY_RATE_DECIMAL_PATTERN.test(rate)) {
    errors.rate = 'Rate must use decimal format.';
  }

  if (!reason) {
    errors.reason = 'Reason is required.';
  }

  return errors;
}

export function validateCurrencyRateCreatePayload(payload) {
  const errors = {};
  const currencyBase = trimValue(payload?.currencyBase).toUpperCase();
  const currency = trimValue(payload?.currency).toUpperCase();
  const rate = trimValue(payload?.rate);
  const reason = trimValue(payload?.reason);

  if (!currencyBase) {
    errors.currencyBase = 'Base currency is required.';
  } else if (!CURRENCY_CODE_PATTERN.test(currencyBase)) {
    errors.currencyBase = 'Currency code must use 3 letters.';
  }

  if (!currency) {
    errors.currency = 'Currency is required.';
  } else if (!CURRENCY_CODE_PATTERN.test(currency)) {
    errors.currency = 'Currency code must use 3 letters.';
  } else if (currencyBase && currencyBase === currency) {
    errors.currency = 'Currency pair must use different currencies.';
  }

  if (!rate) {
    errors.rate = 'Rate is required.';
  } else if (!CURRENCY_RATE_DECIMAL_PATTERN.test(rate)) {
    errors.rate = 'Rate must use decimal format.';
  }

  if (!reason) {
    errors.reason = 'Reason is required.';
  }

  return errors;
}

export function validateCurrencyRateResetPayload(payload) {
  const errors = {};

  if (!trimValue(payload?.reason)) {
    errors.reason = 'Reason is required.';
  }

  return errors;
}

export function mapCurrencyRateFieldErrors(requestError) {
  const errors = {};
  const details = Array.isArray(requestError?.errors) ? requestError.errors : [];

  details.forEach((item) => {
    const field = resolveFieldErrorParameter(item?.parameter);
    if (field !== 'currencyBase' && field !== 'currency' && field !== 'rate' && field !== 'reason') {
      return;
    }

    errors[field] = trimValue(item?.message) || 'Invalid value.';
  });

  return errors;
}
